'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { HTTPError } from 'ky';
import { useIsClient } from 'usehooks-ts';
import { useAccount } from 'wagmi';

import AngleDouble from '@uikit/icons/AngleDouble';

import { ONE_INCH_NATIVE_NETWORK_TOKEN_ADDRESS } from '@shared/constants/magicSwap';
import { useSwitchChain } from '@shared/helpers/web3/chain';
import useMagicIdWallet from '@shared/hooks/useMagicIdWallet';

import { useGetOneInchNetworksQuery, useGetOneInchQuoteQuery } from '@query/magicSwap';

import { DEFAULT_QUOTE_REFETCH_INTERVAL } from '@magic-swap/components/pages/MagicSwap/constants';
import { getErrorMessage } from '@utilities/errors/getErrorMessage';

import { MaxOutWalletAmountButton } from '../components/MaxOutWalletAmountButton';
import { SwapActionButton } from '../components/SwapActionButton';
import { SwapOperationMetadata } from '../components/SwapOperationMetadata';
import { SwapOperationStatusModal } from '../components/SwapOperationStatusModal';
import type { SwapOperationSide} from '../utilities/SwapExperienceStore';
import { useSwapExperienceStore } from '../utilities/SwapExperienceStore';
import { useSwapOperationExecutor } from '../utilities/SwapTransactionExecutor';
import { useTokenBalance } from '../utilities/useTokenBalance';
import { EstimatedKarmaInfo } from './EstimatedKarmaInfo';
import type { SwapUnitControlError } from './SwapUnitControl';
import { SwapUnitControl } from './SwapUnitControl';

interface ErrorMessageMapping {
  pattern: RegExp;
  message: string;
  tooltip?: string;
}

const WELLKNOWN_API_ERROR_MESSSAGES: Array<ErrorMessageMapping> = [
  {
    pattern: /swap amount too small/i,
    message: 'Amount too small to swap',
  },
  {
    pattern: /token not supported/i,
    message: 'The selected pair of tokens is not available for cross-chain swap',
  },
];

const translateWellKnownErrorMessage = (message: string): Omit<ErrorMessageMapping, 'pattern'> => {
  return (
    WELLKNOWN_API_ERROR_MESSSAGES.find(({ pattern }) => pattern.test(message)) ?? {
      message: 'Unknown error',
    }
  );
};

const SwapExperience = () => {
  const isClient = useIsClient();

  const { source, destination, getSource, updateSource, updateDestination } =
    useSwapExperienceStore();

  const { userWallet } = useMagicIdWallet();

  const { switchChain } = useSwitchChain();

  const { data: sourceTokenBalance } = useTokenBalance({
    address: userWallet?.attributes.address,
    chainId: source?.network?.id,
    token: source?.token ?? undefined,
  });

  const handleMaxOutWalletBalanceButtonClick = useCallback(() => {
    if (source?.token && sourceTokenBalance !== undefined) {
      updateSource({ amount: sourceTokenBalance });
    }
  }, [source?.token, sourceTokenBalance, updateSource]);

  const handleSourceTokenChange = useCallback(
    (updates: Partial<SwapOperationSide>) => {
      updateSource(updates);
    },
    [updateSource],
  );

  const handleDestinationTokenChange = useCallback(
    (updates: Partial<SwapOperationSide>) => {
      updateDestination(updates);
    },
    [updateDestination],
  );

  const handleNetworkChangedByUser = useCallback(async () => {
    if (source?.network?.id) {
      console.log('handleNetworkChangedByUser: Updating wallet network to', {
        'source?.network?.id': source?.network?.id,
      });

      await switchChain({
        destChainId: source?.network?.id,
        terminateConnectionOnError: false,
      });
    }
  }, [source?.network?.id, switchChain]);

  useEffect(() => {
    handleNetworkChangedByUser();
  }, [source?.network?.id, handleNetworkChangedByUser]);

  const { chain } = useAccount();

  const { data: networks } = useGetOneInchNetworksQuery();

  useEffect(() => {
    const currentSource = getSource();

    if (chain?.id && currentSource.network?.id !== chain?.id && networks) {
      console.log('update source because network changed externally', {
        'currentSource.network?.id': currentSource.network?.id,
        'chain?.id': chain?.id,
      });

      updateSource({
        network: networks.find(({ id }) => id === chain.id),
        token: null,
        amount: 0n,
      });
    }
  }, [getSource, chain?.id, networks, updateSource]);

  const {
    data: quote,
    isFetching: isQuoteQueryRefetching,
    error: quoteQueryError,
  } = useGetOneInchQuoteQuery(
    {
      fromNetworkId: source?.network?.id,
      toNetworkId: destination?.network?.id,
      fromTokenAddress: source?.token?.id,
      toTokenAddress: destination?.token?.id,
      amount: source?.token && source?.amount ? source.amount.toString() : undefined,
      walletAddress: userWallet?.attributes.address,
    },
    {
      enabled: !!(
        source?.network &&
        destination?.network &&
        source?.token &&
        destination?.token &&
        source?.amount
      ),
      refetchInterval: DEFAULT_QUOTE_REFETCH_INTERVAL,
    },
  );

  useEffect(() => {
    if (quoteQueryError) {
      updateDestination({
        amount: 0n,
      });
    } else {
      if (quote && destination?.token?.attributes.decimal) {
        updateDestination({
          amount: BigInt(quote.dstAmount),
        });
      }
    }
  }, [updateDestination, quote, quoteQueryError, destination?.token?.attributes.decimal]);

  const invertSwapSides = useCallback(() => {
    updateSource({ ...destination, amount: 0n });
    updateDestination({ ...source, amount: 0n });
  }, [source, destination, updateSource, updateDestination]);

  const { operations, purgeSwapOperation } = useSwapOperationExecutor();

  const handleCloseSwapOperationStatusModalClose = useCallback(() => {
    purgeSwapOperation(operations[0].id);
  }, [operations, purgeSwapOperation]);

  const { data: tokenBalance } = useTokenBalance({
    address: userWallet?.attributes.address,
    chainId: source?.network?.id,
    token: source?.token ?? undefined,
  });

  const [isBalanceExceeded, setIsBalanceExceeded] = useState(false);

  useEffect(() => {
    if (source?.amount !== undefined) {
      setIsBalanceExceeded(tokenBalance !== undefined && source.amount > tokenBalance);
    }
  }, [tokenBalance, source.amount]);

  const sourceUnitErrors = useMemo(() => {
    const errors: Array<SwapUnitControlError> = [];

    if (
      !!source.network &&
      !!destination.network &&
      source.network.id !== destination.network.id &&
      source.token?.id === ONE_INCH_NATIVE_NETWORK_TOKEN_ADDRESS
    ) {
      errors.push({
        message: `Swapping native tokens is not supported.`,
        tooltip: `Swapping native tokens is not supported. Swap ${source.token?.attributes.symbol} to wrapped ${source.token?.attributes.symbol} first and then swap wrapped ${source.token?.attributes.symbol} to ${destination.token?.attributes.symbol}.`,
      });
    }

    if (
      source?.amount !== undefined &&
      tokenBalance !== undefined &&
      source.amount > tokenBalance
    ) {
      errors.push({
        message: `There's not enough ${source?.token?.attributes.symbol} in your wallet`,
      });
    }

    return errors;
  }, [source, destination, tokenBalance]);

  const destinationUnitErrors = useMemo(() => {
    const errors: Array<SwapUnitControlError> = [];

    if (!quoteQueryError) {
      return errors;
    }

    const rawErrorMessage = getErrorMessage(quoteQueryError);

    if (quoteQueryError instanceof HTTPError) {
      errors.push(translateWellKnownErrorMessage(rawErrorMessage));
    } else {
      errors.push({ message: rawErrorMessage });
    }

    return errors;
  }, [quoteQueryError]);

  return (
    <div className="flex flex-col gap-[32px]">
      <div>
        <SwapUnitControl
          label="You pay"
          data={source}
          headerControls={
            isClient &&
            userWallet?.attributes.address && (
              <MaxOutWalletAmountButton onClick={handleMaxOutWalletBalanceButtonClick} />
            )
          }
          onChange={handleSourceTokenChange}
          error={sourceUnitErrors[0]}
        />

        <div className="flex flex-row items-center justify-center overflow-visible h-[16px]">
          <div className="flex flex-row items-center justify-center size-[60px] bg-neutral-50 rounded-full">
            <button className="flex flex-row items-center justify-center size-[48px] bg-primary-100 rounded-full">
              <AngleDouble
                onClick={invertSwapSides}
                className="icon-[24px] rotate-90 text-secondary-700"
              />
            </button>
          </div>
        </div>

        <SwapUnitControl
          label="You receive"
          data={destination}
          isReadOnly={true}
          //  isClient here prevents blinking upon initial rendering on the server
          isDataStale={isClient && isQuoteQueryRefetching}
          onChange={handleDestinationTokenChange}
          error={destinationUnitErrors[0]}
        />
      </div>

      <div className="flex flex-col gap-[32px]">
        <SwapActionButton
          disabled={
            isBalanceExceeded ||
            !!quoteQueryError ||
            sourceUnitErrors.length > 0 ||
            destinationUnitErrors.length > 0
          }
        />

        <SwapOperationMetadata />

        <EstimatedKarmaInfo
          estimatedKarma={quote?.estimatedKarma || 0}
          isDataStale={isClient && isQuoteQueryRefetching}
        />
      </div>

      <SwapOperationStatusModal
        open={!!operations[0]}
        operation={operations[0]}
        onClose={handleCloseSwapOperationStatusModalClose}
      />
    </div>
  );
};

export { SwapExperience };
